/**
 * TPL cart-drawer promo header — client-side sync.
 *
 * Why this exists: the complementary upsell host (`<tpl-promo-cart-upsell>`) is
 * marked `data-skip-subtree-update`, so when the cart section morphs after AJAX
 * changes, Shopify does not replace its inner HTML — initial Liquid output
 * would otherwise stay forever.
 *
 * We read the cart via `getCanonicalCartSnapshot()` (shared `/cart.js` with
 * shipping + upsell) because many `cart:update` dispatches (notably
 * `CartAddEvent` from `product-form.js`) put **quantity added in that request**
 * in `detail.data.itemCount`, not the cart total — only the canonical cart
 * matches Liquid’s `cart.item_count` for threshold logic.
 *
 * @typedef {object} DrawerHeaderConfig
 * @property {number} cap
 * @property {string} headingLte - May include `[[count]]` and `[[item_word]]` (items still needed before the line-count threshold is passed).
 * @property {string} [headingLteNounSingular] - Word for `[[item_word]]` when count is 1.
 * @property {string} [headingLteNounPlural] - Word for `[[item_word]]` when count is 2+.
 * @property {string} subheadingLte
 * @property {string} headingGt
 * @property {string} subheadingGt
 * @property {boolean} fewItemsUseInfoIcon
 * @property {boolean} showLeadIcon
 */

import { ThemeEvents } from '@theme/events';
import { getCanonicalCartSnapshot } from './tpl-cart-canonical.js';

const tplDrawerHeaderGlobal = /** @type {typeof globalThis & Record<string, boolean>} */ (globalThis);
const INSTALL_KEY = '__tplDrawerHeaderCartJs';

/**
 * @param {Element} root - Host with `data-tpl-drawer-promo`
 * @returns {DrawerHeaderConfig | null}
 */
function readConfig(root) {
  const el = root.querySelector('script[type="application/json"][data-tpl-drawer-header-config]');
  if (!el?.textContent) return null;
  try {
    return /** @type {DrawerHeaderConfig} */ (JSON.parse(el.textContent.trim()));
  } catch {
    return null;
  }
}

/**
 * Lead icons are authored once in Liquid (`<template data-tpl-drawer-lead-svg>`).
 *
 * @param {Element} root
 * @param {'check' | 'info'} variant
 * @returns {string}
 */
function leadSvgMarkup(root, variant) {
  const tpl = root.querySelector(`template[data-tpl-drawer-lead-svg="${variant}"]`);
  return tpl?.innerHTML.trim() ?? '';
}

/**
 * Matches Liquid: `(cap + 1) - cart.item_count`, floored at 1.
 *
 * @param {string} template
 * @param {number} itemCount
 * @param {number} cap
 * @param {string} [nounSingular]
 * @param {string} [nounPlural]
 * @returns {string}
 */
function interpolateHeadingLte(template, itemCount, cap, nounSingular, nounPlural) {
  const max = Number.isFinite(cap) ? cap : 2;
  const remaining = Math.max(1, max + 1 - itemCount);
  const sg = nounSingular || 'artikel';
  const pl = nounPlural || 'artikelen';
  const itemWord = remaining === 1 ? sg : pl;
  let out = (template ?? '').split('[[count]]').join(String(remaining));
  out = out.split('[[item_word]]').join(itemWord);
  return out;
}

/**
 * @param {Element} root
 * @param {number} itemCount - Must match Liquid: Shopify `cart.item_count`
 * @param {DrawerHeaderConfig} cfg
 */
function apply(root, itemCount, cfg) {
  const cap = Number(cfg.cap);
  const max = Number.isFinite(cap) ? cap : 2;
  const few = itemCount <= max;

  const heading = few
    ? interpolateHeadingLte(cfg.headingLte, itemCount, max, cfg.headingLteNounSingular, cfg.headingLteNounPlural)
    : cfg.headingGt;
  const sub = few ? cfg.subheadingLte : cfg.subheadingGt;

  const headingEl = root.querySelector('.tpl-promo-banner__heading');
  const subEl = root.querySelector('.tpl-promo-banner__subheading');
  const inner = root.querySelector('.tpl-promo-banner__inner');
  const iconWrap = root.querySelector('.tpl-promo-banner__lead-icon');

  if (headingEl) headingEl.textContent = heading ?? '';
  if (subEl) subEl.textContent = sub ?? '';

  if (inner) {
    if (few) inner.setAttribute('data-tpl-promo-few-items', '');
    else inner.removeAttribute('data-tpl-promo-few-items');
  }

  if (iconWrap && cfg.showLeadIcon) {
    const useInfo = few && cfg.fewItemsUseInfoIcon;
    const variant = useInfo ? 'info' : 'check';
    const svg = leadSvgMarkup(root, variant);
    if (svg) iconWrap.innerHTML = svg;
  }
}

/**
 * @param {number} itemCount
 */
function syncAllRoots(itemCount) {
  document.querySelectorAll('[data-tpl-drawer-promo]').forEach((host) => {
    const cfg = readConfig(host);
    if (!cfg) return;
    apply(host, itemCount, cfg);
  });
}

async function refreshFromCart() {
  const roots = document.querySelectorAll('[data-tpl-drawer-promo]');
  if (!roots.length) return;

  try {
    const cart = await getCanonicalCartSnapshot();
    const n = typeof cart.item_count === 'number' ? cart.item_count : 0;
    syncAllRoots(n);
  } catch {
    /* ignore */
  }
}

function onCartUpdate() {
  refreshFromCart();
}

if (!tplDrawerHeaderGlobal[INSTALL_KEY]) {
  tplDrawerHeaderGlobal[INSTALL_KEY] = true;
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
}
